import React, { useState } from 'react';
import PropTypes from 'prop-types';
import '../../styles/simpleOfferLayout.css';
import SimpleArtistMenu from './SimpleArtistMenu';
import SimplePoolToggle from './SimplePoolToggle';
import { initials } from '../../utils/offerHeaderHelpers';
import { poolMemberKey } from '../../utils/buildPoolEngineInput';

const ROLE_LABEL = {
  headline: 'Headliner',
  support: 'Support',
};

const STATUS_LABEL = {
  draft: 'Draft',
  sent: 'Sent',
  accepted: 'Confirmed',
  declined: 'Declined',
};

const STATUS_CLASS = {
  draft: 'simple-lineup-status simple-lineup-status--draft',
  sent: 'simple-lineup-status simple-lineup-status--sent',
  accepted: 'simple-lineup-status simple-lineup-status--confirmed',
  declined: 'simple-lineup-status simple-lineup-status--declined',
};

function LineupRow({ entry, ctrl, onEdit, payoutAmount }) {
  const role = ROLE_LABEL[entry.act_type] || entry.act_type || 'Unassigned';
  const status = STATUS_LABEL[entry.state] || entry.state || '';
  const statusClass = STATUS_CLASS[entry.state] || 'simple-lineup-status';
  const payout = ctrl.formatCalculatedNumber(payoutAmount);
  // The lineup payload has no per-row venue; all siblings share the offer's
  // venue, so source it from the offer/confirm.
  const venueName = entry.venue_name || ctrl.offer?.venue?.name || ctrl.confirm?.venue?.name || '';
  // Avatar URLs can be missing or broken; fall back to initials on error.
  const [imgOk, setImgOk] = useState(Boolean(entry.performer_avatar_url));
  // The change-artist modal (V3OfferEditModals) edits the ACTIVE offer's
  // primary performer, so the pencil only renders on the row for the offer this
  // page is currently editing — matching the V3 deal-terms header's single
  // change-artist entry point.
  const isActiveOffer = entry.id === ctrl.offer?.id;

  return (
    <div className="simple-lineup-row">
      <div className="simple-lineup-row__avatar">
        {imgOk
          ? <img src={entry.performer_avatar_url} alt="" className="simple-lineup-row__avatar-img" onError={() => setImgOk(false)} />
          : <span className="simple-lineup-row__avatar-initials">{initials(entry.performer_name)}</span>}
      </div>
      <div className="simple-lineup-row__info">
        <div className="simple-lineup-row__name-row">
          <span className="simple-lineup-row__name">{entry.performer_name || 'Unnamed artist'}</span>
          {isActiveOffer && ctrl.openChangeArtist && (
            <button
              type="button"
              className="simple-lineup-row__change-artist"
              onClick={ctrl.openChangeArtist}
              aria-label="Change artist"
              title="Change artist"
              data-testid="simple-change-artist"
            >
              <i className="far fa-pen" aria-hidden="true" />
            </button>
          )}
          <span className="simple-lineup-role-pill">{role}</span>
          <span className={statusClass}>{status}</span>
        </div>
        {venueName && <div className="simple-lineup-row__subtitle">{venueName}</div>}
      </div>
      <div className="simple-lineup-row__actions">
        <button
          type="button"
          className="simple-lineup-row__offer-btn"
          onClick={() => ctrl.handlePreview(entry.id)}
          aria-label={`Preview offer for ${entry.performer_name || 'artist'}`}
        >
          <i className="far fa-eye" aria-hidden="true" />
          {' Offer'}
        </button>
        <SimpleArtistMenu ctrl={ctrl} row={entry} onEdit={onEdit} />
        <div className="simple-lineup-row__payout">
          <span className="simple-lineup-row__payout-label">Payout</span>
          <span className="simple-lineup-row__payout-value">{payout}</span>
        </div>
      </div>
    </div>
  );
}

LineupRow.propTypes = {
  entry: PropTypes.shape({
    id: PropTypes.number,
    act_type: PropTypes.string,
    performer_name: PropTypes.string,
    performer_avatar_url: PropTypes.string,
    state: PropTypes.string,
    dollar_amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    venue_name: PropTypes.string,
  }).isRequired,
  ctrl: PropTypes.object.isRequired,
  onEdit: PropTypes.func,
  payoutAmount: PropTypes.number,
};

LineupRow.defaultProps = {
  onEdit: () => {},
  payoutAmount: 0,
};

const SimpleLineupSection = ({ ctrl, onCreateDeal, onAddSupport, onEdit }) => {
  const lineup = ctrl.offer?.lineup || [];
  // Partition every lineup entry into exactly one bucket so the rendered rows
  // always match the Deals tab count. Entries whose act_type is neither
  // 'headline' nor 'support' — i.e. no role set yet, whether that's a deal with
  // no artist at all or one whose artist was assigned without an act_type — fall
  // into a third "Unassigned" (role-unassigned) card instead of being silently
  // dropped (O-4167). `is_headliner` is the backend-canonical flag; we also
  // accept act_type === 'headline' so callers passing only act_type bucket right.
  const isHeadliner = (e) => Boolean(e.is_headliner) || e.act_type === 'headline';
  const headliners = lineup.filter(isHeadliner);
  const supports = lineup.filter((e) => !isHeadliner(e) && e.act_type === 'support');
  const unassigned = lineup.filter((e) => !isHeadliner(e) && e.act_type !== 'support');

  const poolActive = !!ctrl.poolActive;
  const takeByKey = new Map((ctrl.calculated?.poolMembers || []).map((m) => [m.performerId, m]));
  const payoutFor = (entry) => {
    if (poolActive) {
      const key = poolMemberKey(entry);
      const m = takeByKey.get(key);
      return m ? Number(m.take) || 0 : 0;
    }
    return Number(entry.dollar_amount) || 0;
  };

  const headlinerTotal = headliners.reduce((sum, e) => sum + payoutFor(e), 0);
  const supportTotal = supports.reduce((sum, e) => sum + payoutFor(e), 0);
  const unassignedTotal = unassigned.reduce((sum, e) => sum + payoutFor(e), 0);

  return (
    <div className="simple-lineup-section">
      <SimplePoolToggle team={ctrl.team} offer={ctrl.offer} />

      {/* Headliners card */}
      <div className="simple-offer-card simple-lineup-card">
        <div className="simple-offer-card__header">
          <h3 className="simple-offer-card__title">Headliners</h3>
        </div>
        {headliners.length === 0 ? (
          <div className="simple-lineup-card__empty">No headliners yet.</div>
        ) : (
          headliners.map((entry) => (
            <LineupRow key={entry.id} entry={entry} ctrl={ctrl} onEdit={onEdit} payoutAmount={payoutFor(entry)} />
          ))
        )}
        <div className="simple-lineup-card__footer">
          <span className="simple-lineup-card__footer-label">Total Headliner Payout</span>
          <span className="simple-lineup-card__footer-value">{ctrl.formatCalculatedNumber(headlinerTotal)}</span>
        </div>
      </div>

      {/* Support card */}
      <div className="simple-offer-card simple-lineup-card">
        <div className="simple-offer-card__header">
          <h3 className="simple-offer-card__title">Support</h3>
        </div>
        {supports.length === 0 ? (
          <div className="simple-lineup-card__empty">
            No support acts yet.{' '}
            <button
              type="button"
              className="simple-lineup-card__add-support"
              onClick={onAddSupport}
            >
              + Add support
            </button>
          </div>
        ) : (
          supports.map((entry) => (
            <LineupRow key={entry.id} entry={entry} ctrl={ctrl} onEdit={onEdit} payoutAmount={payoutFor(entry)} />
          ))
        )}
        <div className="simple-lineup-card__footer">
          <span className="simple-lineup-card__footer-label">Total Support Cost</span>
          <span className="simple-lineup-card__footer-value">{ctrl.formatCalculatedNumber(supportTotal)}</span>
        </div>
      </div>

      {/* Unassigned card — only shown when a lineup entry has no headliner/support
          role yet (e.g. a deal created before an artist was assigned). Rendering
          it keeps the Deals tab count and the list in agreement (O-4167). */}
      {unassigned.length > 0 && (
        <div className="simple-offer-card simple-lineup-card">
          <div className="simple-offer-card__header">
            <h3 className="simple-offer-card__title">Unassigned</h3>
          </div>
          {unassigned.map((entry) => (
            <LineupRow key={entry.id} entry={entry} ctrl={ctrl} onEdit={onEdit} payoutAmount={payoutFor(entry)} />
          ))}
          <div className="simple-lineup-card__footer">
            <span className="simple-lineup-card__footer-label">Total Unassigned Payout</span>
            <span className="simple-lineup-card__footer-value">{ctrl.formatCalculatedNumber(unassignedTotal)}</span>
          </div>
        </div>
      )}

      {/* Right-aligned "Create Artist Deal" action, sitting below the lineup
          cards and above the Event Details card in the Deals tab. */}
      <div className="simple-lineup-section__create-row">
        <button
          type="button"
          className="simple-lineup-section__create-btn"
          onClick={onCreateDeal}
        >
          + Add Artist
        </button>
      </div>
    </div>
  );
};

SimpleLineupSection.propTypes = {
  ctrl: PropTypes.object.isRequired,
  onCreateDeal: PropTypes.func,
  onAddSupport: PropTypes.func,
  onEdit: PropTypes.func,
};

SimpleLineupSection.defaultProps = {
  onCreateDeal: () => {},
  onAddSupport: () => {},
  onEdit: () => {},
};

export default SimpleLineupSection;
